<template>
  <navbar-menu-tool v-model="computedValue" v-bind="uploadDataBind">
    <template #content>
      <v-row no-gutters class="pa-3">
        <v-col cols="12">
          <sidebar-form
            ref="params-form"
            :is-valid.sync="paramsValid"
            :inputs="inputs"
            v-model="params"
            is-editing-on
            :has-icon-column="false"
            hide-rules-icon
          >
            <template #file>
              <file-input
                ref="file"
                :options="dropzoneOptions"
                show-added-files
                :events="{
                  'vdropzone-file-added': onFileAdded,
                  'vdropzone-removed-file': reset,
                  'vdropzone-error-multiple': clearFile,
                }"
              />
            </template>
          </sidebar-form>
          <template v-if="isFileLoaded && params.separator && params.encoding">
            <v-row no-gutters>
              <v-checkbox v-model="hasAttributesMapping" class="ma-0 pb-2" hide-details :disabled="loading">
                <template #label>
                  <span class="text-body-2 font--text"> {{ $i18n.t('table.dataImport.attributesMapping') }} </span>
                </template>
              </v-checkbox>
            </v-row>
            <sidebar-form
              v-if="hasAttributesMapping"
              ref="attributes-form"
              :inputs="attributesInputs"
              v-model="attributesMapping"
              is-editing-on
              :has-icon-column="false"
              hide-rules-icon
            />
          </template>
        </v-col>
      </v-row>
    </template>
    <template #content.footer>
      <base-button
        class="ml-3 px-7"
        translation-path="dialog.body.preview"
        :disabled="!paramsValid || !isFileLoaded || loadingImport"
        :loading="loadingPreview"
        @click="importPreview"
        v-bind="{
          ...(previewActive
            ? {
                buttonStyle: {
                  backgroundColor: `${$_colors.primarybackground} !important`,
                  border: '1px solid rgba(26, 115, 232, 0.3) !important',
                  color: $_colors.primary,
                },
                color: $_colors.primarybackground,
                iconColor: $_colors.primary,
              }
            : {
                buttonStyle: {
                  backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                  border: '1px solid rgba(0, 0, 0, 0.2) !important',
                  color: '#61646d',
                },
                color: 'rgba(225,225,225,1)',
                iconColor: 'rgba(0,0,0,0.7)',
              }),
        }"
      />
      <base-button
        class="px-7"
        translation-path="dialog.agreeImport"
        :disabled="!paramsValid || !isFileLoaded || loadingPreview"
        :loading="loadingImport"
        @click="importUploadConfirm"
      />
      <main-dialog
        v-if="confirmationDialogLoaded"
        :is-visible.sync="confirmationDialogVisible"
        :title="$i18n.t('table.dataImport.confirmationTitle')"
        :body="$i18n.t('table.dataImport.confirmationBody', { name: layers[lid]?.name })"
        @agree="importUpload"
        :loading="loadingImport"
        agree-text="dialog.agreeImport"
      />
    </template>
  </navbar-menu-tool>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { transform } from 'ol/proj';

import FileInput from '@/components/FileInput';
import NavbarMenuTool from '@/components/NavbarMenuTool';
import SidebarForm from '@/components/SidebarForm';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'UploadAttributesMenu',
  components: { FileInput, NavbarMenuTool, SidebarForm, MainDialog: () => import('@/components/MainDialog') },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    coordinatesEpsgsArray() {
      return Object.values(this.coordinatesEpsgs).map(value => ({ value: value.srid, text: value.text }));
    },
    dropzoneOptions() {
      return {
        url: '/',
        autoProcessQueue: false,
        acceptedFiles: '.csv',
        addRemoveLinks: true,
        maxFiles: 1,
        init: function() {
          this.on('maxfilesexceeded', file => {
            this.removeAllFiles();
            this.addFile(file);
          });
        },
      };
    },
    loading() {
      return this.loadingImport || this.loadingPreview;
    },
    inputs() {
      return [
        {
          name: 'file',
          dataType: { name: 'text' },
          translationPath: 'table.dataImport.params.file',
        },
        ...(this.isFileLoaded
          ? [
              {
                name: 'separator',
                rules: [v => !!v || this.$i18n.t('rules.required')],
                dataType: { name: 'select' },
                items: [
                  { text: this.$i18n.t('chars.tabulator'), value: 'tabspace' },
                  { text: this.$i18n.t('chars.semicolon'), value: ';' },
                  { text: this.$i18n.t('chars.comma'), value: ',' },
                  { text: this.$i18n.t('chars.verticalLine'), value: '|' },
                  { text: this.$i18n.t('chars.space'), value: ' ' },
                ],
                translationPath: 'table.dataImport.params.separator',
                hideDetails: true,
                alignClass: 'center',
                readonly: this.loading,
              },
              {
                name: 'encoding',
                rules: [v => !!v || this.$i18n.t('rules.required')],
                dataType: { name: 'select' },
                items: [
                  {
                    value: 'utf-8',
                    text: 'UTF-8',
                  },
                  {
                    value: 'windows-1250',
                    text: 'Windows-1250',
                  },
                  {
                    value: 'iso-8859-2',
                    text: 'ISO 8859-2',
                  },
                ],
                translationPath: 'table.dataImport.params.encoding',
                hideDetails: true,
                alignClass: 'center',
                readonly: this.loading,
              },
              {
                name: 'x_column',
                rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
                dataType: { name: 'select' },
                items: this.csvHeaders,
                translationPath: 'table.dataImport.params.xColumn',
                hideDetails: true,
                alignClass: 'center',
                readonly: this.loading,
                disabled: !this.params.separator || !this.params.encoding,
              },
              {
                name: 'y_column',
                rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
                dataType: { name: 'select' },
                items: this.csvHeaders,
                translationPath: 'table.dataImport.params.yColumn',
                hideDetails: true,
                alignClass: 'center',
                readonly: this.loading,
                disabled: !this.params.separator || !this.params.encoding,
              },
              {
                name: 'source_srs',
                rules: [v => !!v || this.$i18n.t('rules.required')],
                dataType: { name: 'select' },
                items: this.coordinatesEpsgsArray,
                translationPath: 'table.dataImport.params.sourceSrs',
                hideDetails: true,
                alignClass: 'center',
                readonly: this.loading,
              },
            ]
          : []),
      ];
    },
    uploadDataBind() {
      return {
        menuData: { name: 'tableImport', position: 'left', allowOtherTop: true },
        buttonBind: { verboseName: this.$i18n.t('menu.uploadData') },
        hideActivator: true,
        hasCloseHeader: true,
        hasFooter: true,
      };
    },

    layers: get('layers/layers'),
    datasources: get('layers/metadata'),
    lid() {
      return this.$route.params.lid;
    },
    datasourceName() {
      return this.layers?.[this.lid]?.data_source_name;
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    attributesInputs: [],
    attributesMapping: {},
    confirmationDialogLoaded: false,
    confirmationDialogVisible: false,
    csvHeaders: [],
    defaultParams: {
      x_column: '',
      y_column: '',
      source_srs: '',
      // separator: ';',
      // encoding: 'utf-8',
      separator: '',
      encoding: '',
    },
    hasAttributesMapping: false,
    isFileLoaded: false,
    loadingImport: false,
    loadingPreview: false,
    params: {},
    paramsValid: false,
    previewActive: false,
  }),
  methods: {
    uploadCsvToDataSource: call('layers/uploadCsvToDataSource'),
    clearFile() {
      this.$refs.file?.$refs.dropzone.removeAllFiles();
      this.isFileLoaded = false;
    },
    onFileAdded(file) {
      if (file.name.split('.').at(-1) !== 'csv') return;
      this.getCsvData(file);
    },
    getCsvData(file, onlyCsvDataRefresh = false) {
      const reader = new FileReader();
      reader.readAsText(file, this.params['encoding']);
      reader.onloadend = () => {
        if (file.status === 'error') return;
        const fileData = reader.result;
        const fileHeaders = fileData.split('\n')[0].split(this.params['separator']);
        if (!onlyCsvDataRefresh) this.setDefaultParams();
        this.csvHeaders = fileHeaders.map((header, index) => {
          return {
            value: index,
            text: header,
          };
        });
        this.setAttributesMapping();
        this.isFileLoaded = true;
      };
    },
    reset() {
      this.hidePreview();
      this.clearFile();
      this.setDefaultParams();
      this.$refs['params-form']?.$refs?.form.resetValidation();
    },
    setDefaultParams() {
      this.params = { ...this.defaultParams };
      this.hasAttributesMapping = false;
      this.attributesMapping = {};
    },
    setAttributesMapping() {
      const attributes = this.$_flattenStructure(
        this.getForm(
          this.layers?.[this.lid]?.form_schema?.elements || [],
          this.datasources?.[this.datasourceName]?.attributes_schema?.attributes || []
        )
      )?.filter(attribute => !attribute.readOnly && attribute.editable);
      this.attributesMapping = attributes.reduce((acc, attribute) => {
        acc[attribute.attribute] = null;
        return acc;
      }, {});
      this.attributesInputs = attributes.map(attribute => {
        return {
          name: attribute.attribute,
          dataType: { name: 'select' },
          items: this.csvHeaders,
          straightTitle: attribute.label,
          hideDetails: true,
          alignClass: 'center',
          readonly: this.loading,
        };
      });
    },
    hidePreview() {
      this.$root.$emit('clearVisibleGeomLayerAndFitView', 'tableImportPreview');
      this.previewActive = false;
    },
    async importPreview(value = !this.previewActive) {
      if (value) {
        try {
          this.loadingPreview = true;
          const file = this.$refs.file?.$refs.dropzone?.getAcceptedFiles()[0];
          const reader = new FileReader();
          reader.onload = evt => {
            const arr = this.$papa.parse(evt.target.result).data;
            arr.shift();
            const geojson = {
              type: 'FeatureCollection',
              features: arr.map(row => {
                return {
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: transform(
                      [parseFloat(row[this.params.x_column]), parseFloat(row[this.params.y_column])],
                      `EPSG:${this.params.source_srs}`,
                      this.$_config.defaultEpsg
                    ),
                  },
                  properties: {},
                };
              }),
              crs: { type: 'name', properties: { name: `EPSG:${this.$_config.defaultEpsg}` } },
            };
            this.$root.$emit('createFeaturesLayer', geojson, 'tableImportPreview');
            this.previewActive = true;
          };
          reader.readAsText(file);
        } finally {
          this.loadingPreview = false;
        }
      } else {
        this.hidePreview();
        return;
      }
    },
    importUploadConfirm() {
      this.confirmationDialogLoaded = this.confirmationDialogVisible = true;
    },
    async importUpload() {
      try {
        this.loadingImport = true;
        const formData = new FormData();
        const file = this.$refs.file?.$refs.dropzone?.getAcceptedFiles()[0];
        formData.append('file', file);
        Object.keys(this.params).forEach(key => {
          formData.append(key, this.params[key]);
        });
        formData.append(
          'attributes',
          JSON.stringify(
            Object.entries(this.hasAttributesMapping ? this.attributesMapping : {}).reduce((acc, [key, value]) => {
              if (value !== null) acc[key] = value;
              return acc;
            }, {})
          )
        );
        await this.uploadCsvToDataSource({ formData, datasourceName: this.datasourceName });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('table.dataImport.confirmationSuccess', { name: this.layers[this.lid]?.name }),
        });
        this.importPreview(false);
        this.reset();
        this.$root.$emit('refreshTableData');
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === this.datasourceName)
          .map(layer => layer.id);
        if (layersIds?.length) {
          this.$root.$emit(
            'refreshMvtSources',
            layersIds.map(layer => ({ id: layer, group: 'layers' })),
            true
          );
          this.$root.$emit('updateLegendStylesCounts', { layersIds });
        }
      } finally {
        this.confirmationDialogVisible = false;
        this.loadingImport = false;
      }
    },
  },
  watch: {
    lid: {
      handler(nV, oV) {
        if (nV !== oV) {
          this.reset();
          this.computedValue = false;
        }
        this.setAttributesMapping();
      },
      immediate: true,
    },
    params: {
      handler() {
        this.importPreview(false);
      },
      deep: true,
    },
    value(nV) {
      if (!nV) this.reset();
    },
  },
  mounted() {
    this.setDefaultParams();
    this.$watch(
      vm => [vm.params.separator, vm.params.encoding],
      (nV, oV) => {
        if (oV[0] === nV[0] && oV[1] === nV[1]) return;
        const file = this.$refs.file?.$refs.dropzone?.getAcceptedFiles()[0];
        if (file) {
          this.getCsvData(file, true);
        }
      },
      {
        deep: true,
      }
    );
  },
};
</script>
